import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { WindDirectionEnumDto } from "@weather/data/dto/weather-day.dto";
import type { WeatherDto } from "@weather/data/dto/weather.dto";
import { WeatherDtoSchema } from "@weather/data/dto/weather.dto";
import {
    WeatherDay,
    WindDirectionEnum,
} from "@weather/domain/models/weather-day.model";
import { WeatherHour } from "@weather/domain/models/weather-hour.model";
import { Weather } from "@weather/domain/models/weather.model";
import { injectable } from "inversify";
import { err, Result } from "neverthrow";

const ONE_HUNDRED = 100;

const WIND_DIRECTION_BY_DTO: Readonly<
    Record<WindDirectionEnumDto, WindDirectionEnum>
> = {
    N: WindDirectionEnum.N,
    NE: WindDirectionEnum.NE,
    E: WindDirectionEnum.E,
    SE: WindDirectionEnum.SE,
    S: WindDirectionEnum.S,
    SW: WindDirectionEnum.SW,
    W: WindDirectionEnum.W,
    NW: WindDirectionEnum.NW,
};

@injectable()
export class WeatherMapper {
    map(weatherDto: WeatherDto): Result<Weather, ModelError<typeof Weather>> {
        const weatherDtoParsed = WeatherDtoSchema.safeParse(weatherDto);

        if (!weatherDtoParsed.success) {
            console.error(weatherDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const { days, hours } = weatherDto.forecast;

        const mappedDays = days.map((dayDto) => {
            const dayProps = {
                date: dayDto.date,
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                etc: dayDto.etc === null ? null : Number(dayDto.etc.toFixed(2)),
                eto: dayDto.eto ?? 0,
                kc: dayDto.kc ?? 0,
                humidity: Number((dayDto.humidity * ONE_HUNDRED).toFixed(0)),
                windSpeedFrom: Number(dayDto.wind_speed_from.toFixed(0)),
                windSpeedTo: Number(dayDto.wind_speed_to.toFixed(0)),
                windDirection: WIND_DIRECTION_BY_DTO[dayDto.wind_direction],
                sunset: dayDto.sunset,
                sunrise: dayDto.sunrise,
                icon: dayDto.icon,
                rainProb: Number((dayDto.rain_prob * ONE_HUNDRED).toFixed(0)),
                rainFall: Number(dayDto.rain_fall.toFixed(0)),
                minTemp: Number(dayDto.min_temp.toFixed(0)),
                maxTemp: Number(dayDto.max_temp.toFixed(0)),
            };
            return WeatherDay.create(dayProps);
        });

        const mappedHours = hours.map((hourDto) => {
            const hourProps = {
                etc: hourDto.etc ?? null,
                humidity: Number((hourDto.humidity * ONE_HUNDRED).toFixed(0)),
                hour: hourDto.hour,
                icon: hourDto.icon,
                rainProb: Number((hourDto.rain_prob * ONE_HUNDRED).toFixed(0)),
                rainFall: Number(hourDto.rain_fall.toFixed(0)),
                temperature: hourDto.temperature,
                windDirection: hourDto.wind_direction,
                windSpeedFrom: Number(hourDto.wind_speed_from.toFixed(0)),
                windSpeedTo: Number(hourDto.wind_speed_to.toFixed(0)),
            };
            return WeatherHour.create(hourProps);
        });

        const validDays = Result.combine(mappedDays).unwrapOr([]);
        const validHours = Result.combine(mappedHours).unwrapOr([]);

        return Weather.createWithRecovery({
            stationId: weatherDto.forecast.station_id,
            stationName: weatherDto.forecast.station_name,
            stationTz: weatherDto.forecast.station_tz ?? null,
            days: validDays,
            hours: validHours,
        });
    }
}
